/**
 * Deployed Drops Tab
 * Shows all SMSWiser drops (live/deployed) – every new drop appears here.
 * Same content as DropsPage, embedded as a tab within the country stats section.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { Rocket } from 'lucide-react';
import { useLocation } from 'react-router-dom';

import { Button } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/utils';
import { useSmswiserDrops, useCountry } from '@/hooks';
import { DROPS_FETCH_LENGTH_DEFAULT } from '@trafficpulse/shared';
import type { DropStatusLabel } from '@/types/smswiser-drops';
import {
  EMPTY_DROPS_FILTERS,
  useDropsFiltering,
  type DropsFilterState,
} from '@/features/drops/hooks/useDropsFiltering';
import DropsToolbar from '@/features/drops/components/DropsToolbar';
import DropsStatusPills, { type DropsStatusFilter } from '@/features/drops/components/DropsStatusPills';
import { DropsAdvancedFilters } from '@/features/drops/components/filters';
import DropsList from '@/features/drops/components/DropsList';
import DropsLoadingPanel from '@/features/drops/components/DropsLoadingPanel';
import { getDropsErrorHint } from '@/features/drops/utils/drops-error-hint';
import { useAutoFetchDropsOnDateRange } from '@/features/drops/hooks/useAutoFetchDropsOnDateRange';
import { resolveDropsFetchLength } from '@/features/drops/utils/resolveDropsFetchLength';
import { getDropsCoverageWarning } from '@/features/drops/utils/dropsBatchCoverage';
import { shouldShowDropsListLoading } from '@/features/drops/utils/loading';
import {
  DEFAULT_DROPS_SORT,
  toggleDropsSort,
  type DropsSortKey,
  type DropsSortState,
} from '@/features/drops/utils/sort';

function ymdToIsoStart(ymd: string): string | undefined {
  return ymd ? `${ymd}T00:00:00.000Z` : undefined;
}

function ymdToIsoEnd(ymd: string): string | undefined {
  return ymd ? `${ymd}T23:59:59.999Z` : undefined;
}

interface DeployedDropsTabProps {
  /** Optional: pre-filter drops by country (e.g. "australia" → matches AU, Australia) */
  countryKey?: string;
}

export const DeployedDropsTab: FC<DeployedDropsTabProps> = ({ countryKey }) => {
  const { pathname, search } = useLocation();
  const returnTo = useMemo(() => {
    const params = new URLSearchParams(search);
    params.set('tab', 'deployed');
    return `${pathname}?${params.toString()}`;
  }, [pathname, search]);

  // Scope drops to this country via SMSWiser's native `landCode` filter (e.g. "AU").
  // This is the correct request: the REST API has no free-text country search — the
  // legacy `search` term only does a client-side title-substring match, and drop
  // titles are prefixed with the land code (AU_…), never the country name, so a
  // name search returns nothing. The parent page already calls useCountry(countryKey)
  // so this hits the React Query cache.
  const { data: countryData } = useCountry(countryKey ?? '');
  const countryLandCode = countryData?.landCode?.trim() || undefined;
  // Fallback name search only when the land code is unknown (defensive; also keeps
  // the legacy scraper path working, which does have server-side search).
  const countrySearch = useMemo(() => {
    if (countryLandCode) return '';
    if (countryData?.countryName) return countryData.countryName;
    return countryKey?.replace(/_/g, ' ').trim() ?? '';
  }, [countryLandCode, countryData?.countryName, countryKey]);

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<DropsFilterState>(EMPTY_DROPS_FILTERS);
  const [fetchLengthDraft, setFetchLengthDraft] = useState(DROPS_FETCH_LENGTH_DEFAULT);
  const [fetchLength, setFetchLength] = useState(DROPS_FETCH_LENGTH_DEFAULT);
  const [fetchedRange, setFetchedRange] = useState<{ from: string; to: string }>({
    from: '',
    to: '',
  });
  const [sort, setSort] = useState<DropsSortState>(DEFAULT_DROPS_SORT);
  const [page, setPage] = useState(1);
  const [slowLoading, setSlowLoading] = useState(false);

  useAutoFetchDropsOnDateRange({
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    fetchedRange,
    setFetchLength,
    setFetchLengthDraft,
    setFetchedRange,
    setPage,
  });

  // Pass landCode so SMSWiser's native filter returns drops for THIS country
  // (server-side) instead of the newest 100 across all countries.
  const { data, isLoading, isError, error, refetch, isFetching } = useSmswiserDrops({
    search: countrySearch,
    landCode: countryLandCode,
    start: 0,
    length: fetchLength,
    from: ymdToIsoStart(fetchedRange.from),
    to: ymdToIsoEnd(fetchedRange.to),
  });

  const drops = data?.data ?? [];
  const recordsTotal = data?.recordsTotal ?? 0;

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setSlowLoading(false);
      return;
    }
    const t = window.setTimeout(() => setSlowLoading(true), 2000);
    return () => window.clearTimeout(t);
  }, [isLoading, isFetching]);

  const {
    filteredDrops,
    pagedDrops,
    page: activePage,
    totalPages,
  } = useDropsFiltering({
    drops,
    filters,
    pageSize: 100,
    sort,
    page,
    setPage,
  });

  const handleSort = useCallback((key: DropsSortKey) => {
    setSort((current) => toggleDropsSort(current, key));
  }, []);

  const handlePageChange = useCallback(
    (next: number) => setPage(next),
    [setPage],
  );

  const handleFetch = useCallback(() => {
    const effectiveLength = resolveDropsFetchLength({
      manualLength: fetchLengthDraft,
      dateFrom: filters.dateFrom,
      dateTo: filters.dateTo,
    });
    setFetchLength(effectiveLength);
    setFetchLengthDraft(effectiveLength);
    setFetchedRange({ from: filters.dateFrom, to: filters.dateTo });
    setPage(1);
    refetch();
  }, [fetchLengthDraft, filters.dateFrom, filters.dateTo, refetch, setPage]);

  const coverageWarning = useMemo(
    () =>
      getDropsCoverageWarning({
        drops,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        isFetching,
      }),
    [drops, filters.dateFrom, filters.dateTo, isFetching],
  );

  const errorText = useMemo(() => getApiErrorMessage(error, ''), [error]);

  const errorHint = useMemo(
    () =>
      getDropsErrorHint(errorText, {
        dateRangeFetch: Boolean(fetchedRange.from && fetchedRange.to),
      }),
    [errorText, fetchedRange.from, fetchedRange.to],
  );

  const dateRangeActive = Boolean(filters.dateFrom && filters.dateTo);

  const showListLoading = shouldShowDropsListLoading({
    isLoading,
    isFetching,
    fetchedFrom: fetchedRange.from,
    fetchedTo: fetchedRange.to,
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Rocket className="h-4 w-4 text-primary" />
        Deployed
      </div>
      <p className="text-xs text-muted-foreground">
        All SMSWiser drops (live) – every new deployed drop appears here. Use Fetch to load more.
      </p>

      <div className="flex flex-col gap-3">
        <DropsToolbar
          search={filters.search}
          onSearchChange={(value) => setFilters((f) => ({ ...f, search: value }))}
          fetchLengthDraft={fetchLengthDraft}
          onFetchLengthDraftChange={(value) =>
            setFetchLengthDraft(Number.isFinite(value) ? value : DROPS_FETCH_LENGTH_DEFAULT)
          }
          onFetch={handleFetch}
          isFetching={isFetching}
          dateRangeActive={dateRangeActive}
          advancedOpen={filtersOpen}
          onToggleAdvanced={() => setFiltersOpen((v) => !v)}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <DropsStatusPills
            value={filters.status as DropsStatusFilter}
            onChange={(value) =>
              setFilters((f) => ({ ...f, status: value as 'all' | DropStatusLabel }))
            }
          />
          <div className="text-xs text-muted-foreground">
            Showing <span className="font-medium text-foreground">{filteredDrops.length}</span> matching
            of <span className="font-medium text-foreground">{drops.length}</span> loaded
            {recordsTotal ? (
              <>
                {' '}
                • SMSWiser total:{' '}
                <span className="font-medium text-foreground">{recordsTotal.toLocaleString()}</span>
              </>
            ) : null}
          </div>
        </div>

        <DropsAdvancedFilters
          open={filtersOpen}
          campaign={filters.campaign}
          onCampaignChange={(value) => setFilters((f) => ({ ...f, campaign: value }))}
          leadsList={filters.leadsList}
          onLeadsListChange={(value) => setFilters((f) => ({ ...f, leadsList: value }))}
          sender={filters.sender}
          onSenderChange={(value) => setFilters((f) => ({ ...f, sender: value }))}
          country={filters.country}
          onCountryChange={(value) => setFilters((f) => ({ ...f, country: value }))}
          dateFrom={filters.dateFrom}
          onDateFromChange={(value) => setFilters((f) => ({ ...f, dateFrom: value }))}
          dateTo={filters.dateTo}
          onDateToChange={(value) => setFilters((f) => ({ ...f, dateTo: value }))}
          onClear={() => setFilters(EMPTY_DROPS_FILTERS)}
          shownCount={filteredDrops.length}
          loadedCount={drops.length}
          totalSmswiser={recordsTotal}
          fetchEstimate={dateRangeActive ? fetchLengthDraft : undefined}
          coverageWarning={coverageWarning}
        />
      </div>

      <div className="min-h-[280px] min-w-0 flex-1 overflow-auto">
        {showListLoading ? (
          <DropsLoadingPanel
            className="flex min-h-[240px] w-full items-center justify-center"
            fetchLength={fetchLength}
            fetchedFrom={fetchedRange.from}
            fetchedTo={fetchedRange.to}
            slowLoading={slowLoading}
            variant="deployed"
          />
        ) : isError ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground mb-3">Failed to load drops</p>
            <p className="text-xs text-muted-foreground mb-4">{errorText || 'Unknown error'}</p>
            {errorHint ? (
              <p className="text-xs text-muted-foreground mb-4">{errorHint}</p>
            ) : null}
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : pagedDrops.length > 0 ? (
          <DropsList
            drops={pagedDrops}
            page={activePage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            returnTo={returnTo}
            sortBy={sort.sortBy}
            sortDir={sort.sortDir}
            onSort={handleSort}
          />
        ) : (
          <div className="flex min-h-[200px] items-center justify-center rounded-lg border border-dashed border-border bg-muted/5">
            <div className="text-center px-4">
              <Rocket className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground">No deployed drops in this batch</p>
              <p className="text-xs text-muted-foreground mt-1">
                Adjust filters or Fetch more from SMSWiser.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeployedDropsTab;
